import { useApiContext } from '../components/App/AppApi'
import { AbsenceModel, AbsenceTypeModel, AdditionalPaymentModel, AdditionalPaymentTypeModel, ConfigGlobalModel, ConfigModel, ContinuumFormModel, ContinuumFormSummaryModel, ControlledDocumentModel, ControlledDocumentSummaryModel, HolidayEntitlementAllowanceModel, HolidayEntitlementModel, IncidentCreateRequestModel, IncidentCreateResponseModel, IncidentCreationOptionsModel, MainLayoutParamsModel, PayslipModel, PayslipSummaryModel, ServiceOrderSummaryModel, TimelogClockInOutRequest, TimelogStatusResponse, TokenModel, TrainingRecordModel, TrainingRecordSummaryModel } from '../defs/defs'
import eqxApi, { eqxApiProps } from '../shared/classes/eqxApi'
import eqxEndpoint from '../shared/classes/eqxEndpoint'

export function useApi(api: appApi | null = null) {
    const apiContext = useApiContext()
    if (api) return api
    return apiContext
}

interface StandardError {
    status?: string
    message: string
}
interface StandardSuccess {
    message: string
}
interface StandardSuccessWithData<T> {
    message: string
    data: T
}
interface TokenParams {
    version: string
    aadToken: string
}
interface ConfigParams {

}
interface TenantLookupByDisplayIdParams {
    tenantDisplayID: string
}
interface TenantLookupByDisplayIdResponse {
    tenantID: string
}

/** Go module routes on the v2 API today; shared endpoints will omit this segment later. */
const GO_API = '/continuum-go'

/**
 * here we can place any mofifications to the default behaviour of the standard app
 */
class appApi extends eqxApi {
    tenantID?: string
    environment?: string
    instance?: string

    constructor(props: eqxApiProps & { tenantID?: string, environment?: string, instance?: string }) {
        super(props)
        this.tenantID = props.tenantID
        this.environment = props.environment
        this.instance = props.instance
    }

    setTenantID(tenantID: string) {
        this.tenantID = tenantID
    }

    setEnvironment(environment: string) {
        this.environment = environment
    }

    setInstance(instance: string) {
        this.instance = instance
    }

    endpoints = {
        //auth
        token: () => new eqxEndpoint<TokenModel, TokenParams>(`${GO_API}/token`, 'post'),
        configGlobal: () => new eqxEndpoint<ConfigGlobalModel>(`${GO_API}/config/global`, 'get'),
        config: () => new eqxEndpoint<ConfigModel, ConfigParams>(`${GO_API}/config`, 'post'),
        tenantLookupByDisplayId: () =>
            new eqxEndpoint<TenantLookupByDisplayIdResponse, TenantLookupByDisplayIdParams>(
                `${GO_API}/config/tenants/lookup-by-display-id`,
                'post'
            ),

        //global
        mainLayoutParams: () => new eqxEndpoint<MainLayoutParamsModel>(`${GO_API}/main-layout`, 'get'),
        sendHelpEmail: () => new eqxEndpoint<StandardSuccess, FormData>(`${GO_API}/send-help-email`, 'post'),

        //continuum-forms
        continuumForms: () => new eqxEndpoint<ContinuumFormSummaryModel[]>(`${GO_API}/continuum-forms`, 'get'),
        continuumForm: (id: string) => new eqxEndpoint<ContinuumFormModel>(`${GO_API}/continuum-forms/${id}`, 'get'),
        submitContinuumForm: (id: string) => new eqxEndpoint<StandardSuccess, any>(`${GO_API}/continuum-forms/${id}/submit`, 'post'),

        //controlled-documents
        controlledDocuments: () => new eqxEndpoint<ControlledDocumentSummaryModel[]>(`${GO_API}/controlled-documents`, 'get'),
        controlledDocument: (id: string) =>
            new eqxEndpoint<ControlledDocumentModel>(`${GO_API}/controlled-documents/${id}`, 'get'),
        controlledDocumentContent: (id: string) =>
            new eqxEndpoint<string>(`${GO_API}/controlled-documents/${id}/content`, 'get'),
        acceptControlledDocument: (id: string) =>
            new eqxEndpoint<StandardSuccess>(`${GO_API}/controlled-documents/${id}/accept`, 'post'),

        //training-records
        trainingRecords: () => new eqxEndpoint<TrainingRecordSummaryModel[]>(`${GO_API}/training-records`, 'get'),
        trainingRecord: (id: string) => new eqxEndpoint<TrainingRecordModel>(`${GO_API}/training-records/${id}`, 'get'),
        trainingRecordContent: (id: string) => new eqxEndpoint<string>(`${GO_API}/training-records/${id}/content`, 'get'),
        trainingRecordDocuments: (id: string) => new eqxEndpoint<any[]>(`${GO_API}/training-records/${id}/documents`, 'get'),

        // timelog
        timelogStatus: () => new eqxEndpoint<TimelogStatusResponse>(`${GO_API}/timelog/get-status`, 'get'),
        timelogClockIn: () => new eqxEndpoint<TimelogStatusResponse, TimelogClockInOutRequest>(`${GO_API}/timelog/clock-in`, 'post'),
        timelogClockOut: () => new eqxEndpoint<TimelogStatusResponse, TimelogClockInOutRequest>(`${GO_API}/timelog/clock-out`, 'post'),

        // service-orders
        serviceOrdersToday: () => new eqxEndpoint<ServiceOrderSummaryModel[]>(`${GO_API}/service-orders/today`, 'get'),
        serviceOrdersFuture: () => new eqxEndpoint<ServiceOrderSummaryModel[]>(`${GO_API}/service-orders/future`, 'get'),
        serviceOrder: (id: number | string) => new eqxEndpoint<import('../defs/defs').ServiceOrderModel>(`${GO_API}/service-orders/${id}`, 'get'),

        // absences
        holidayEntitlements: () => new eqxEndpoint<HolidayEntitlementModel[]>(`${GO_API}/absences/holiday-entitlements`, 'get'),
        holidayEntitlementAbsences: (id: number) => new eqxEndpoint<AbsenceModel[]>(`${GO_API}/absences/holiday-entitlement/${id}`, 'get'),
        unassignedAbsences: () => new eqxEndpoint<AbsenceModel[]>(`${GO_API}/absences/unassigned`, 'get'),
        absenceTypes: () => new eqxEndpoint<AbsenceTypeModel[]>(`${GO_API}/absences/types`, 'get'),
        holidayEntitlementAllowance: (id: number) => new eqxEndpoint<HolidayEntitlementAllowanceModel>(`${GO_API}/absences/holiday-entitlement/${id}/allowance`, 'get'),

        // payslips
        payslips: () => new eqxEndpoint<PayslipSummaryModel[]>(`${GO_API}/payslips`, 'get'),
        payslip: (id: string | number) => new eqxEndpoint<PayslipModel>(`${GO_API}/payslips/${id}`, 'get'),
        payslipDocument: (id: string | number) => new eqxEndpoint<string>(`${GO_API}/payslips/${id}/content`, 'get'),

        // additional-payments
        additionalPaymentsPending: () => new eqxEndpoint<AdditionalPaymentModel[]>(`${GO_API}/additional-payments/pending`, 'get'),
        additionalPaymentsProcessed: () => new eqxEndpoint<AdditionalPaymentModel[]>(`${GO_API}/additional-payments/processed`, 'get'),
        additionalPaymentTypes: () => new eqxEndpoint<AdditionalPaymentTypeModel[]>(`${GO_API}/additional-payments/types`, 'get'),

        // incidents
        incidentCreationOptions: () => new eqxEndpoint<IncidentCreationOptionsModel>(`${GO_API}/incidents/creation-options`, 'get'),
        createIncident: () => new eqxEndpoint<IncidentCreateResponseModel, IncidentCreateRequestModel>(`${GO_API}/incidents/create`, 'post'),
    }
}
export {
    appApi as default,
    GO_API,
    type ConfigParams,
    type TenantLookupByDisplayIdParams,
    type TenantLookupByDisplayIdResponse,
    type TokenParams
}

